import { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Bell, BellRing, RefreshCw, ArrowLeft, Inbox, Globe, MapPin, TrendingUp, Users, Lock, LogOut, Check } from "lucide-react";
import { enablePush, pushSupported } from "@/lib/push";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const TOKEN_KEY = "ll_admin_token";

const StatCard = ({ icon: Icon, value, label, small, testid }) => (
  <div data-testid={testid} className="rounded-2xl border border-white/10 bg-[#12233c] p-5">
    <Icon className="h-5 w-5 text-[#38bdf8] mb-3" />
    <div className={`font-display font-extrabold tracking-tight truncate ${small ? "text-lg" : "text-3xl"}`}>{value}</div>
    <div className="text-white/50 text-xs mt-1">{label}</div>
  </div>
);

const ListCard = ({ title, items }) => {
  const max = Math.max(1, ...(items || []).map((i) => i.count));
  return (
    <div className="rounded-2xl border border-white/10 bg-[#12233c] p-5">
      <p className="font-mono text-[11px] uppercase tracking-[0.2em] text-white/40 mb-4">{title}</p>
      {(!items || items.length === 0) && <p className="text-white/30 text-sm">No data yet</p>}
      <div className="space-y-2.5">
        {(items || []).map((i) => (
          <div key={i.name}>
            <div className="flex justify-between text-sm mb-1">
              <span className="text-white/75 truncate pr-2">{i.name}</span>
              <span className="text-white/50">{i.count}</span>
            </div>
            <div className="h-1.5 rounded-full bg-white/10 overflow-hidden">
              <div className="h-full bg-[#2f80ed]" style={{ width: `${(i.count / max) * 100}%` }} />
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

const urgencyStyle = {
  urgent: "text-red-400 border-red-400/30 bg-red-400/10",
  soon: "text-amber-400 border-amber-400/30 bg-amber-400/10",
  standard: "text-white/60 border-white/15 bg-white/5",
};

function LoginGate({ onSuccess }) {
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError("");
    try {
      const { data } = await axios.post(`${API}/admin/login`, { password });
      localStorage.setItem(TOKEN_KEY, data.token);
      localStorage.setItem("ll_no_track", "1");
      onSuccess(data.token);
    } catch (err) {
      const detail = err.response?.data?.detail;
      setError(typeof detail === "string" ? detail : "Login failed. Try again.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <main className="min-h-screen bg-[#0a1526] text-white font-body flex items-center justify-center px-6">
      <form onSubmit={submit} data-testid="admin-login-form" className="w-full max-w-sm rounded-2xl border border-white/10 bg-[#12233c] p-8">
        <div className="flex items-center gap-2 mb-6">
          <Lock className="h-5 w-5 text-[#38bdf8]" />
          <h1 className="font-display font-black text-2xl tracking-tight">Admin Access</h1>
        </div>
        <label className="block text-white/50 text-xs uppercase tracking-[0.2em] font-mono mb-2">Password</label>
        <input
          type="password"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          data-testid="admin-password-input"
          autoFocus
          className="w-full rounded-xl border border-white/15 bg-[#0a1526] px-4 py-3 text-sm outline-none focus:border-[#2f80ed] transition-colors"
          placeholder="Enter admin password"
        />
        {error && <p data-testid="admin-login-error" className="text-red-400 text-sm mt-3">{error}</p>}
        <button
          type="submit"
          disabled={busy || !password}
          data-testid="admin-login-submit"
          className="mt-6 w-full rounded-full bg-[#2f80ed] px-5 py-3 text-sm font-semibold hover:bg-[#4a9bff] transition-colors disabled:opacity-60"
        >
          {busy ? "Signing in…" : "Sign in"}
        </button>
        <Link to="/" className="mt-5 inline-flex items-center gap-2 font-mono text-xs uppercase tracking-[0.2em] text-white/40 hover:text-white transition-colors">
          <ArrowLeft className="h-3.5 w-3.5" /> Back to site
        </Link>
      </form>
    </main>
  );
}

function Dashboard({ token, onLogout }) {
  const [requests, setRequests] = useState([]);
  const [stats, setStats] = useState(null);
  const [tracking, setTracking] = useState(null);
  const [loading, setLoading] = useState(true);
  const [enabling, setEnabling] = useState(false);
  const [perm, setPerm] = useState(typeof Notification !== "undefined" ? Notification.permission : "default");

  const auth = { headers: { Authorization: `Bearer ${token}` } };

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [reqRes, statsRes, trackRes] = await Promise.all([
        axios.get(`${API}/service-request`, auth),
        axios.get(`${API}/analytics`, auth),
        axios.get(`${API}/admin/tracking-status`, auth),
      ]);
      setRequests(reqRes.data);
      setStats(statsRes.data);
      setTracking(trackRes.data);
    } catch (e) {
      if (e.response?.status === 401) {
        onLogout();
        return;
      }
      toast.error("Could not load data.");
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  const excludeMe = async () => {
    try {
      const { data } = await axios.post(`${API}/admin/exclude-ip`, {}, auth);
      setTracking(data);
      toast.success("Done — this network won't be counted anymore.");
    } catch {
      toast.error("Couldn't update. Try again.");
    }
  };

  const resetStats = async () => {
    if (!window.confirm("Reset all analytics? This clears every recorded visit and can't be undone.")) return;
    try {
      await axios.post(`${API}/analytics/reset`, {}, auth);
      toast.success("Analytics reset to zero.");
      load();
    } catch {
      toast.error("Couldn't reset. Try again.");
    }
  };

  useEffect(() => {
    load();
    const t = setInterval(load, 30000);
    return () => clearInterval(t);
  }, [load]);

  const turnOn = async () => {
    setEnabling(true);
    try {
      await enablePush();
      setPerm("granted");
      toast.success("Notifications enabled — you'll get an alert on new requests.");
    } catch (e) {
      toast.error(e.message || "Could not enable notifications.");
    } finally {
      setEnabling(false);
    }
  };

  return (
    <main className="min-h-screen bg-[#0a1526] text-white font-body">
      <div className="max-w-[1100px] mx-auto px-6 lg:px-10 py-14">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-6 mb-12">
          <div>
            <Link to="/" data-testid="admin-back-link" className="inline-flex items-center gap-2 font-mono text-xs uppercase tracking-[0.2em] text-white/40 hover:text-white transition-colors mb-4">
              <ArrowLeft className="h-3.5 w-3.5" /> Back to site
            </Link>
            <h1 className="font-display font-black text-4xl sm:text-5xl tracking-tighter">Dashboard</h1>
            <p className="text-white/50 mt-2">Leads and site traffic — last 30 days.</p>
          </div>

          <div className="flex items-center gap-3">
            <button
              onClick={load}
              data-testid="admin-refresh-btn"
              className="inline-flex items-center gap-2 rounded-full border border-white/15 px-5 py-3 text-sm hover:border-white/40 transition-colors"
            >
              <RefreshCw className={`h-4 w-4 ${loading ? "animate-spin" : ""}`} /> Refresh
            </button>
            <button
              onClick={turnOn}
              disabled={enabling || perm === "granted" || !pushSupported()}
              data-testid="admin-enable-notifications-btn"
              className="inline-flex items-center gap-2 rounded-full bg-[#2f80ed] px-5 py-3 text-sm font-semibold hover:bg-[#4a9bff] transition-colors disabled:opacity-60"
            >
              {perm === "granted" ? <BellRing className="h-4 w-4" /> : <Bell className="h-4 w-4" />}
              {perm === "granted" ? "Notifications On" : enabling ? "Enabling…" : "Enable Notifications"}
            </button>
            <button
              onClick={onLogout}
              data-testid="admin-logout-btn"
              className="inline-flex items-center gap-2 rounded-full border border-white/15 px-5 py-3 text-sm hover:border-white/40 transition-colors"
            >
              <LogOut className="h-4 w-4" /> Log out
            </button>
          </div>
        </div>

        {/* Traffic analytics */}
        <section data-testid="admin-analytics" className="mb-16">
          <div className="flex flex-wrap items-center justify-between gap-3 mb-5">
            <p className="font-mono text-[11px] uppercase tracking-[0.25em] text-[#38bdf8]">Traffic</p>
            <div className="flex items-center gap-2">
              {tracking?.excluded ? (
                <span data-testid="tracking-excluded" className="inline-flex items-center gap-1.5 text-xs text-emerald-400/90 border border-emerald-400/20 bg-emerald-400/10 rounded-full px-3 py-1.5">
                  <Check className="h-3.5 w-3.5" /> Your network is excluded
                </span>
              ) : (
                <button
                  onClick={excludeMe}
                  data-testid="exclude-me-btn"
                  className="text-xs rounded-full border border-white/15 px-3 py-1.5 hover:border-white/40 transition-colors"
                >
                  Stop counting my network
                </button>
              )}
              <button
                onClick={resetStats}
                data-testid="reset-stats-btn"
                className="text-xs rounded-full border border-white/15 px-3 py-1.5 hover:border-red-400/40 hover:text-red-400 transition-colors"
              >
                Reset stats
              </button>
            </div>
          </div>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
            <StatCard icon={Users} value={stats?.total ?? "—"} label="Visits (30d)" testid="stat-total-visits" />
            <StatCard icon={TrendingUp} value={stats?.today ?? "—"} label="Visits today" testid="stat-today-visits" />
            <StatCard icon={Globe} value={stats?.topCountries?.[0]?.name ?? "—"} label="Top country" small testid="stat-top-country" />
            <StatCard icon={MapPin} value={stats?.topCities?.[0]?.name ?? "—"} label="Top city" small testid="stat-top-city" />
          </div>
          <div className="grid md:grid-cols-3 gap-4">
            <ListCard title="Top sources" items={stats?.topSources} />
            <ListCard title="Top countries" items={stats?.topCountries} />
            <ListCard title="Top cities" items={stats?.topCities} />
          </div>
        </section>

        {/* Leads */}
        <p className="font-mono text-[11px] uppercase tracking-[0.25em] text-[#38bdf8] mb-5">Lead Inbox</p>
        {!pushSupported() && (
          <div className="mb-8 rounded-xl border border-amber-400/20 bg-amber-400/5 p-4 text-sm text-amber-300/80">
            Push isn't supported here. Open this page in Chrome/Edge/Safari (or install the app) to receive alerts.
          </div>
        )}

        <div className="space-y-4">
          {loading && requests.length === 0 && <p className="text-white/40">Loading…</p>}
          {!loading && requests.length === 0 && (
            <div className="flex flex-col items-center justify-center py-24 text-center border border-white/10 rounded-2xl">
              <Inbox className="h-10 w-10 text-white/25 mb-4" />
              <p className="text-white/50">No requests yet.</p>
            </div>
          )}
          {requests.map((r) => (
            <div
              key={r.id}
              data-testid={`lead-card-${r.id}`}
              className="rounded-2xl border border-white/10 bg-[#12233c] p-6 hover:border-white/20 transition-colors"
            >
              <div className="flex flex-wrap items-center justify-between gap-3 mb-3">
                <div className="flex items-center gap-3">
                  <h3 className="font-display font-bold text-xl">{r.name}</h3>
                  <span className={`font-mono text-[10px] uppercase tracking-[0.15em] rounded-full border px-2.5 py-1 ${urgencyStyle[r.urgency] || urgencyStyle.standard}`}>
                    {r.urgency}
                  </span>
                </div>
                <span className="font-mono text-xs text-white/40">
                  {new Date(r.created_at).toLocaleString()}
                </span>
              </div>
              <p className="text-[#60A5FA] text-sm font-medium mb-2">{r.service}</p>
              <p className="text-white/70 leading-relaxed mb-4">{r.message}</p>
              <div className="flex flex-wrap gap-x-6 gap-y-1 text-sm text-white/50">
                <a href={`tel:${r.phone}`} className="hover:text-white transition-colors">📞 {r.phone}</a>
                {r.email && <a href={`mailto:${r.email}`} className="hover:text-white transition-colors">✉ {r.email}</a>}
              </div>
            </div>
          ))}
        </div>
      </div>
    </main>
  );
}

export default function Admin() {
  const [token, setToken] = useState(() => localStorage.getItem(TOKEN_KEY) || "");
  const [checking, setChecking] = useState(!!token);

  useEffect(() => {
    if (!token) return;
    axios
      .get(`${API}/admin/verify`, { headers: { Authorization: `Bearer ${token}` } })
      .catch(() => {
        localStorage.removeItem(TOKEN_KEY);
        setToken("");
      })
      .finally(() => setChecking(false));
  }, [token]);

  const logout = () => {
    localStorage.removeItem(TOKEN_KEY);
    setToken("");
  };

  if (checking) {
    return <main className="min-h-screen bg-[#0a1526] text-white flex items-center justify-center">Loading…</main>;
  }
  if (!token) {
    return <LoginGate onSuccess={setToken} />;
  }
  return <Dashboard token={token} onLogout={logout} />;
}
